import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Tag } from "lucide-react";
import { toast } from "sonner";

export default function ExpenseCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/categories").then(r => setCategories(r.data.categories || []))
      .catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ name: "", description: "" }); setEditId(null); setDialog(true); };
  const openEdit = (c) => { setForm({ name: c.name, description: c.description || "" }); setEditId(c.category_id); setDialog(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/categories/${editId}`, form);
        toast.success("Category updated");
      } else {
        await api.post("/categories", form);
        toast.success("Category created");
      }
      setDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try { await api.delete(`/categories/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  return (
    <div data-testid="expense-categories-page">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading font-bold text-2xl text-[#1F2937]">Expense Categories</h1>
          <p className="text-sm text-gray-500 mt-1">{categories.length} categories</p></div>
        <Button data-testid="add-category-btn" onClick={openNew} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm"><Plus className="w-4 h-4 mr-1.5" />Add Category</Button>
      </div>
      {loading ? <div className="animate-pulse h-40 bg-gray-100 rounded-sm" /> : categories.length === 0 ? (
        <Card className="border rounded-sm"><CardContent className="p-12 text-center text-gray-400"><Tag className="w-10 h-10 mx-auto mb-3 opacity-40" /><p>No categories yet</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {categories.map(c => (
            <Card key={c.category_id} className="border rounded-sm">
              <CardContent className="p-4 flex items-center justify-between">
                <div><h3 className="font-medium text-[#1F2937]">{c.name}</h3>{c.description && <p className="text-xs text-gray-500">{c.description}</p>}</div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEdit(c)}><Pencil className="w-3.5 h-3.5 text-gray-400" /></Button>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => remove(c.category_id)}><Trash2 className="w-3.5 h-3.5 text-red-400" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={dialog} onOpenChange={setDialog}><DialogContent>
        <DialogHeader><DialogTitle>{editId ? "Edit" : "Add"} Category</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Name</label><Input data-testid="cat-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
          <div><label className="text-xs font-semibold text-gray-500 uppercase block mb-1">Description</label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
          <Button data-testid="save-cat-btn" onClick={save} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}{editId ? "Update" : "Create"}</Button>
        </DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
